import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Save, X, ArrowLeft, ChevronDown } from 'react-feather';
import config from '../../../../config';
import CustomizerContext from '../../../../_helper/Customizer';
import '../../sadmin.css';

const ConcessionaireEdit = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const { layoutURL } = useContext(CustomizerContext);
    const layout = layoutURL || 'default';

    useEffect(() => {
        if (localStorage.getItem('usertype') !== 'sadmin') {
            alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, []);

    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };
    const login_id = localStorage.getItem('loginId');

    const [concessionaire_name, setName] = useState('');
    const [concessionaire_email, setEmail] = useState('');
    const [concessionaire_phone, setPhone] = useState('');
    const [concessionaire_address, setAddress] = useState('');
    const [concessionaire_username, setUsername] = useState('');
    const [concessionaire_password, setPassword] = useState('');
    const [concessionaire_license_start_date, setLicStart] = useState('');
    const [concessionaire_license_end_date, setLicEnd] = useState('');
    const [concessionaire_active, setActive] = useState(true);
    const [audit_required, setAuditRequired] = useState(false);
    const [audit_tolerance, setAuditTolerance] = useState('');
    const [features_ai_chatbot, setAiChatbot] = useState(true);
    // Org-level module entitlements. Both default OFF: these are new modules,
    // and the backend gate is fail-open on an absent flag, so the UI must not
    // present an unset flag as enabled.
    const [features_digital_twin, setDigitalTwin] = useState(false);
    const [features_floor_plan_import, setFloorPlanImport] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        axios
            .get(`${config.hostname}/concessionaire/${id}`, configheader)
            .then((res) => {
                const d = res.data || {};
                setName(d.concessionaire_name || '');
                setEmail(d.concessionaire_email || '');
                setPhone(d.concessionaire_phone || '');
                setAddress(d.concessionaire_address || '');
                setUsername(d.concessionaire_username || '');
                setPassword(d.concessionaire_password || '');
                if (d.concessionaire_license_start_date) {
                    setLicStart(new Date(d.concessionaire_license_start_date).toISOString().split('T')[0]);
                }
                if (d.concessionaire_license_end_date) {
                    setLicEnd(new Date(d.concessionaire_license_end_date).toISOString().split('T')[0]);
                }
                setActive(!!d.concessionaire_active);
                setAuditRequired(d.audit_required || false);
                setAuditTolerance(d.audit_tolerance || '');
                setAiChatbot(d.features?.ai_chatbot ?? true);
                setDigitalTwin(d.features?.digital_twin === true);
                setFloorPlanImport(d.features?.floor_plan_import === true);
            })
            .catch((err) => {
                console.error(err);
                toast.error(t('Error loading'));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const goList = () =>
        navigate(`${process.env.PUBLIC_URL}/controlpanel/concessionaire/viewconcessionaire/${layout}`);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (audit_required && !audit_tolerance) {
            toast.error(t('Please enter audit tolerance'));
            return;
        }
        const data = {
            concessionaire_name,
            concessionaire_email,
            concessionaire_phone,
            concessionaire_address,
            concessionaire_username,
            concessionaire_password,
            concessionaire_license_start_date,
            concessionaire_license_end_date,
            concessionaire_active,
            login_id,
            audit_required,
            features: {
                ai_chatbot: features_ai_chatbot,
                digital_twin: features_digital_twin,
                // Never leave the importer on behind a disabled twin.
                floor_plan_import: features_digital_twin && features_floor_plan_import,
            },
        };
        if (audit_required) data.audit_tolerance = audit_tolerance;

        setSubmitting(true);
        try {
            await axios.post(`${config.hostname}/concessionaire/updateconcessionaire/${id}`, data, configheader);
            toast.success(t('Successfully updated'));
        } catch (err) {
            console.error(err);
            toast.error(t('Error'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="v3 v3-sadmin">
            <header className="v3-sadmin-hero">
                <div className="v3-sadmin-hero-text">
                    <div className="v3-label">{t('Edit concessionaire')}</div>
                    <h1 className="v3-display v3-sadmin-greeting" style={{ fontSize: 26 }}>
                        {concessionaire_name || t('Edit')}
                    </h1>
                </div>
                <div className="v3-sadmin-hero-actions">
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={goList}>
                        <ArrowLeft size={14} />
                        {t('Back to list')}
                    </button>
                </div>
            </header>

            <section className="v3-card v3-sadmin-form-card">
                <form className="v3-sadmin-form" onSubmit={handleSubmit} autoComplete="off">
                    <div className="v3-sadmin-form-grid">
                        <div className="v3-field">
                            <label className="v3-label">{t('Name')}</label>
                            <input
                                className="v3-input"
                                type="text"
                                value={concessionaire_name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('Email')}</label>
                            <input
                                className="v3-input"
                                type="email"
                                value={concessionaire_email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('Phone')}</label>
                            <input
                                className="v3-input"
                                type="text"
                                value={concessionaire_phone}
                                onChange={(e) => setPhone(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('License start')}</label>
                            <input
                                className="v3-input"
                                type="date"
                                value={concessionaire_license_start_date}
                                onChange={(e) => setLicStart(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('License end')}</label>
                            <input
                                className="v3-input"
                                type="date"
                                value={concessionaire_license_end_date}
                                onChange={(e) => setLicEnd(e.target.value)}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('Status')}</label>
                            <div className="v3-select-wrap">
                                <select
                                    className="v3-input v3-select"
                                    value={concessionaire_active ? 'true' : 'false'}
                                    onChange={(e) => setActive(e.target.value === 'true')}
                                >
                                    <option value="true">{t('Active')}</option>
                                    <option value="false">{t('Inactive')}</option>
                                </select>
                                <ChevronDown size={14} className="v3-select-caret" aria-hidden />
                            </div>
                        </div>
                        <div className="v3-field" style={{ gridColumn: '1 / -1' }}>
                            <label className="v3-label">{t('Address')}</label>
                            <input
                                className="v3-input"
                                type="text"
                                value={concessionaire_address}
                                onChange={(e) => setAddress(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="v3-sadmin-form-section">
                        <div className="v3-sadmin-form-section-title">{t('Audit settings')}</div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <label className="v3-sadmin-checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={audit_required}
                                    onChange={(e) => setAuditRequired(e.target.checked)}
                                />
                                {t('Enable audit')}
                            </label>
                            <div className="v3-field" style={{ flex: 1, minWidth: 200 }}>
                                <input
                                    className="v3-input"
                                    type="number"
                                    placeholder={t('Audit tolerance (%)')}
                                    value={audit_tolerance}
                                    disabled={!audit_required}
                                    onChange={(e) => setAuditTolerance(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="v3-sadmin-form-section">
                        <div className="v3-sadmin-form-section-title">{t('Modules')}</div>
                        <label className="v3-sadmin-checkbox-row">
                            <input
                                type="checkbox"
                                checked={features_ai_chatbot}
                                onChange={(e) => setAiChatbot(e.target.checked)}
                            />
                            {t('Enable AI chatbot')}
                        </label>
                        <label className="v3-sadmin-checkbox-row">
                            <input
                                type="checkbox"
                                checked={features_digital_twin}
                                onChange={(e) => setDigitalTwin(e.target.checked)}
                            />
                            {t('Enable Digital Twin (3D floor plan)')}
                        </label>
                        {/*
                          * Importing is a separate right from viewing: it rewrites the
                          * published plan that the map, the routing and the unit list all
                          * read from. A leasing team can be entitled to read the model
                          * without being able to redraw it, so this is disabled -- and
                          * forced off -- unless the twin itself is on.
                          */}
                        <label className="v3-sadmin-checkbox-row">
                            <input
                                type="checkbox"
                                checked={features_floor_plan_import}
                                disabled={!features_digital_twin}
                                onChange={(e) => setFloorPlanImport(e.target.checked)}
                            />
                            {t('Allow importing floor plans')}
                        </label>
                    </div>

                    <div className="v3-sadmin-form-actions">
                        <button type="button" className="v3-btn v3-btn-ghost" onClick={goList}>
                            <X size={14} />
                            {t('Cancel')}
                        </button>
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={submitting}>
                            <Save size={14} />
                            {submitting ? t('Updating…') : t('Update')}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
};

export default ConcessionaireEdit;
